import React, { FC, ReactNode } from 'react';
import FormBlock from 'src/components/FormBlock';
import { TransportType } from 'src/types/transport';
import Transport from './Transport';
import { OnChangeMultipleType } from '.';
import { ProviderCompanyType } from 'src/types/providerCompany';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import { TransportChangelogType } from 'src/types/transportDetail';
import Invoice from './Invoice';
import Detail from './Detail';
import { sizes } from 'src/themes/default';
import { UserServiceCode } from 'src/types/user';
import { EditKeysType, progressStepsCreate } from './constants';
import Progress from './Progress';
import Recipient from './Recipient';
import Sender from './Sender';
import translate from '@translate';
import { styled } from '@mui/system';
import HistoryBlock from './HistoryBlock';
import ProgressNew from './ProgressNew';
import Button from 'src/components/Button';
import { Stack } from '@mui/material';
import { PaidActionOptionType } from 'src/components/PaymentEdit';
import NewPackageBlock from './NewPackageBlock';
import PackagesTable from './PackagesTable';
import ApiCallsBlock from './ApiCallsBlock';
import EventsBlock from './EventsBlock';
import LinkedOrdersTable from './LinkedOrdersTable';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { TransportApiCallType, TransportEventType } from 'src/types/transportDetail';

export const StyledSwitchLine = styled('div')({
    margin: '20px 0px',
});

export interface RenderComponentsPropsType {
    data: TransportType | null;
    onChange: OnChangeType<EditKeysType>;
    onChangeMultiple: OnChangeMultipleType;
    onSetProviderCompany: (type: 'recipient' | 'sender') => void;
    providerCompaniesData: null | ProviderCompanyType[];
    editData: TransportType;
    editErrors: FormErrorsType;
    newTransport: boolean;
    disableEdit: boolean;
    permissionEditAll: boolean;
    allowedStatuses: ConstantsCodeType[];
    transportStatusCodes: ConstantsCodeType[];
    boolNumberCodes: ConstantsCodeType[];
    currencyCodes: ConstantsCodeType[];
    paymentCodes: ConstantsCodeType[];
    shipperCodes: ConstantsCodeType[];
    serviceCodes: ConstantsCodeType[];
    transportTypeCodes: ConstantsCodeType[];
    providerServiceCodes: UserServiceCode[];
    activeTab: string;
    handlePaidTransport: (paidOption: PaidActionOptionType) => void;
    formRef: React.RefObject<HTMLFormElement>;
    changelog: TransportChangelogType | null;
    transportStatusCodesObject: ConstantsCodesObjectType;
    paymentCodesObject: ConstantsCodesObjectType;
    shipperCodesObject: ConstantsCodesObjectType;
    serviceCodesObject: ConstantsCodesObjectType;
    lineStateCodesObject: ConstantsCodesObjectType;
    boolNumberCodesObject: ConstantsCodesObjectType;
    transportTypeCodesObject: ConstantsCodesObjectType;
    handleChangeTab: (value: string, requireValidation?: boolean) => void;
    handleSave: () => void;
    handleShowShipperLabel: () => void;
    handleDownloadShipperLabel: () => void;
    requestPickupDate?: (shipper: string) => void;
    skladonUser?: number;
    packageTypeCodes: ConstantsCodeType[];
    apiCalls: TransportApiCallType[] | null;
    events: TransportEventType[] | null;
    shipper?: string | null;
}

const RenderComponents: FC<RenderComponentsPropsType> = (props) => {
    const renderTransport = (summary?: boolean) => (
        <form ref={props.formRef}>
            <Progress data={props.data} />
            <FormBlock>
                <Detail
                    onChange={props.onChange}
                    onChangeMultiple={props.onChangeMultiple}
                    editData={props.editData}
                    editErrors={props.editErrors}
                    disableEdit={props.disableEdit}
                    transportStatusCodes={props.transportStatusCodes}
                    transportTypeCodes={props.transportTypeCodes}
                    boolNumberCodes={props.boolNumberCodes}
                    allowedStatuses={props.allowedStatuses}
                    newTransport={props.newTransport}
                    summary={summary}
                />
            </FormBlock>
            <FormBlock sx={{ marginTop: sizes.blocks_top_MG }}>
                <Invoice
                    onChange={props.onChange}
                    editData={props.editData}
                    editErrors={props.editErrors}
                    disableEdit={props.disableEdit}
                    permissionEditAll={props.permissionEditAll}
                    currencyCodes={props.currencyCodes}
                    paymentCodes={props.paymentCodes}
                    handlePaidTransport={props.handlePaidTransport}
                    summary={summary}
                />
            </FormBlock>
            <FormBlock sx={{ marginTop: sizes.blocks_top_MG }}>
                <Transport
                    onChange={props.onChange}
                    onChangeMultiple={props.onChangeMultiple}
                    editData={props.editData}
                    editErrors={props.editErrors}
                    disableEdit={props.disableEdit}
                    permissionEditAll={props.permissionEditAll}
                    shipperCodes={props.shipperCodes}
                    serviceCodes={props.serviceCodes}
                    providerServiceCodes={props.providerServiceCodes}
                    newTransport={props.newTransport}
                    handleShowShipperLabel={props.handleShowShipperLabel}
                    handleDownloadShipperLabel={props.handleDownloadShipperLabel}
                    requestPickupDate={props.requestPickupDate}
                    summary={summary}
                    linkedOrders={props.data?.ORDERS}
                    skladonUser={props.skladonUser}
                />
            </FormBlock>
            {!summary && <LinkedOrdersTable orders={props.data?.ORDERS} />}
        </form>
    );

    const renderAddress = (summary?: boolean) => (
        <form ref={props.formRef}>
            <FormBlock>
                <Recipient
                    onChange={props.onChange}
                    onSetProviderCompany={() => props.onSetProviderCompany('recipient')}
                    providerCompaniesData={props.providerCompaniesData}
                    editData={props.editData}
                    editErrors={props.editErrors}
                    disableEdit={props.disableEdit}
                    permissionEditAll={props.permissionEditAll}
                    summary={summary}
                />
            </FormBlock>
            <FormBlock>
                <Sender
                    onChange={props.onChange}
                    onSetProviderCompany={() => props.onSetProviderCompany('sender')}
                    providerCompaniesData={props.providerCompaniesData}
                    editData={props.editData}
                    editErrors={props.editErrors}
                    disableEdit={props.disableEdit}
                    permissionEditAll={props.permissionEditAll}
                    summary={summary}
                />
            </FormBlock>
        </form>
    );

    const renderPackages = (summary?: boolean) => (
        <>
            {!props.disableEdit && !summary && (
                <FormBlock sx={{ marginTop: sizes.blocks_top_MG }}>
                    <NewPackageBlock
                        data={props.editData.PACKAGES || []}
                        onChange={(value) => props.onChange('PACKAGES', value, false)}
                        packageTypeCodes={props.packageTypeCodes}
                    />
                </FormBlock>
            )}
            <form ref={props.formRef}>
                <PackagesTable
                    data={props.editData.PACKAGES || []}
                    editErrors={props.editErrors}
                    newTransport={props.newTransport}
                    onChange={(value) => props.onChange('PACKAGES', value, false)}
                    disableEdit={props.disableEdit || !!summary}
                    transportStatusCodes={props.transportStatusCodes}
                    packageTypeCodes={props.packageTypeCodes}
                    shipper={props.shipper || props.editData.SHIPPER}
                />
            </form>
        </>
    );

    const renderHistory = () => (
        <HistoryBlock
            changelog={props.changelog}
            transportStatusCodesObject={props.transportStatusCodesObject}
            paymentCodesObject={props.paymentCodesObject}
            shipperCodesObject={props.shipperCodesObject}
            serviceCodesObject={props.serviceCodesObject}
            lineStateCodesObject={props.lineStateCodesObject}
            boolNumberCodesObject={props.boolNumberCodesObject}
            transportTypeCodesObject={props.transportTypeCodesObject}
            skladonUser={props.skladonUser}
        />
    );

    const renderApiCalls = () => <ApiCallsBlock data={props.apiCalls} />;

    const renderEvents = () => <EventsBlock data={props.events} />;

    const renderStepsMovement = () => {
        const activeStepIndex = progressStepsCreate.findIndex((item) => item.key === props.activeTab);
        return (
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="center">
                {!!activeStepIndex && (
                    <Button
                        variant="outlined"
                        onClick={() => props.handleChangeTab(progressStepsCreate[activeStepIndex - 1].key)}
                    >
                        {translate('transportDetail.action.prevStep')}
                    </Button>
                )}
                {activeStepIndex + 1 !== progressStepsCreate.length && (
                    <Button onClick={() => props.handleChangeTab(progressStepsCreate[activeStepIndex + 1].key, true)}>
                        {translate('transportDetail.action.nextStep')}
                    </Button>
                )}
                {activeStepIndex + 1 === progressStepsCreate.length && (
                    <Button onClick={() => props.handleSave()}>{translate('transportDetail.action.create')}</Button>
                )}
            </Stack>
        );
    };

    if (!props.newTransport) {
        switch (props.activeTab) {
            case 'transport':
                return renderTransport();
            case 'address':
                return renderAddress();
            case 'packages':
                return renderPackages();
            case 'apiCalls':
                return renderApiCalls();
            case 'events':
                return renderEvents();
            case 'history':
            default:
                return renderHistory();
        }
    } else {
        let renderTab: ReactNode = null;
        switch (props.activeTab) {
            case 'transport':
                renderTab = renderTransport();
                break;
            case 'address':
                renderTab = renderAddress();
                break;
            case 'packages':
                renderTab = renderPackages();
                break;
            case 'summary':
            default:
                renderTab = (
                    <>
                        {renderTransport(true)}
                        {renderAddress(true)}
                        {renderPackages(true)}
                    </>
                );
                break;
        }
        return (
            <>
                <ProgressNew activeTab={props.activeTab} />
                {renderTab}
                {renderStepsMovement()}
            </>
        );
    }
};

export default RenderComponents;
